// src/components/ScientificNameDialog/Sections/RelationshipSection.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
} from '@mui/material';
import ScientificNameSelector from "../parts/ScientificNameSelector";

const SectionRelationship  = ({
  form,
  handleChange,
  errors,
  names,
  validNameRadio,
  handleValidNameRadio,
  typeTaxaRadio,
  handleTypeTaxaRadio,
  fetchNameIds,
}) => {
  const currentIdTrimmed = form.id?.trim() || '...';
  const [nameOptions, setNameOptions] = useState(names || []);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (q) => {
    if (!fetchNameIds) return;
    if (!q || q.length < 2) return;
    try {
      setLoading(true);
      const res = await fetchNameIds(q);
      setNameOptions(Array.isArray(res) ? res : []);
    } catch (e) {
      console.warn("SectionRelationship search failed", e);
    } finally {
      setLoading(false);
    }
  }, [fetchNameIds]);

  useEffect(() => {
    if (Array.isArray(names) && names.length) {
      setNameOptions(names);
    }
  }, [names]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'teal' }}>
        Relationships & Status
      </Typography>

      {/* Valid Name Relationship */}
      <FormControl component="fieldset" margin="dense" fullWidth>
        <FormLabel component="legend">Is this the Valid Name?</FormLabel>
        <RadioGroup
          row
          name="validNameRadio"
          value={validNameRadio}
          onChange={handleValidNameRadio}
        >
          <FormControlLabel
            value="own"
            control={<Radio />}
            label={`Yes (Uses current ID: ${currentIdTrimmed})`}
            disabled={!form.id?.trim()}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label="No, specify other Valid Name ID below"
          />
        </RadioGroup>
      </FormControl>

      {validNameRadio === 'other' && (
        <Autocomplete
          options={(nameOptions || []).filter(n => n !== form.id?.trim())} // Exclude self from options
          value={nameOptions.find((n) => n === form.valid_name_id) || null}
          onChange={(e, v) => handleChange('valid_name_id', v || null)}
          onInputChange={(_, v) => runSearch(v)}
          isOptionEqualToValue={(option, value) => option === value}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Valid Name ID (Required if 'No' selected)"
              margin="dense"
              fullWidth
              required
              error={errors.validNameId} // Use error flag
              helperText={errors.validNameId ? 'Valid Name ID must be selected when "No" is chosen.' : ''}
            />
          )}
          sx={{ mb: 2 }}
        />
      )}

      {/* Type Taxa Relationship */}
      <FormControl component="fieldset" margin="dense" fullWidth>
        <FormLabel component="legend">
          Type Designation (For genus-group names and above)
        </FormLabel>
        <RadioGroup
          row
          name="typeTaxaRadio"
          value={typeTaxaRadio}
          onChange={handleTypeTaxaRadio}
        >
          <FormControlLabel
            value="own"
            control={<Radio />}
            label={`This name itself is the type (ID: ${currentIdTrimmed})`}
            disabled={!form.id?.trim()}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label="The type is a different name (specify ID below)"
          />
        </RadioGroup>
      </FormControl>

      {typeTaxaRadio === 'other' && (
        <ScientificNameSelector
          label="Type Taxon ID (Required if 'Different name' selected)"
          valueId={form.type_taxa_id || ""}
          value={form.type_taxa_id || ""}
          isRequired
          disabled={false}
          onChange={(id) => handleChange('type_taxa_id', id || null)}
          onRefreshData={fetchNameIds}
          showInputLabel
          inlineAdd
          sx={{ mb: 1 }}
        />
      )}
    </Box>
  );
};

export default SectionRelationship;
